import {
  Component,
  ChangeDetectionStrategy,
  ViewChild,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';
import { FormMode } from 'app/core/constants/form-mode';
import { AppSettings } from '@services/settings/app-settings.service';
import { CatalogService } from '@services/data/catalog.service';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { PartyFormComponent } from '../../../management-parties/components/party-form/party-form.component';
import { PartyOverviewService } from '../../../management-parties/pages/party-overview/party-overview.service';
import { RequestFormComponet } from '../request-form/request-form.componet';
import { StepApprovalModalComponent, StepApprovalPayload } from '../step-approval-modal/step-approval-modal.component';
import { BpmService } from '../../services/bpm.service';
import { Request } from '../../services/requests.service';

const TRANSFER_SLUG = 'TRANSFER';

@Component({
  selector: 'app-request-transfer-create',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PartyFormComponent,
    RequestFormComponet,
    StepApprovalModalComponent,
    MobileFooterComponent
  ],
  providers: [PartyOverviewService],
  templateUrl: './request-transfer-create.component.html',
  styleUrls: ['./request-transfer-create.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class RequestTransferCreateComponent {
  partyData = input.required<any>();
  partyForm = input.required<FormGroup>();
  currentEntityCode = input<number | null>(null);
  processSlug = input<string>(TRANSFER_SLUG);

  onClose = output<void>();
  onSubmitted = output<void>();

  @ViewChild(StepApprovalModalComponent) approvalModal!: StepApprovalModalComponent;

  readonly appSettings = inject(AppSettings);
  readonly formMode = FormMode;
  private bpmService = inject(BpmService);
  private catalogService = inject(CatalogService);
  private partyOverviewService = inject(PartyOverviewService);
  private fb = inject(FormBuilder);

  transferForm: FormGroup = this.fb.group({
    targetEntityCode: [null, Validators.required],
    processSlug: [TRANSFER_SLUG],
    referenceUuid: [null],
    reason: [null]
  });

  isSubmitting = signal(false);
  approvalRequest = signal<Request | null>(null);

  constructor() {
    queueMicrotask(() => this._syncFormDefaults());
  }

  private _syncFormDefaults(): void {
    const slug = this.processSlug() || TRANSFER_SLUG;
    this.transferForm.patchValue({
      processSlug: slug,
      referenceUuid: this.partyData()?.uuid ?? null
    });
  }

  cancel(): void {
    this.onClose.emit();
  }

  async submitTransfer(): Promise<void> {
    if (this.isSubmitting()) return;
    this._syncFormDefaults();

    if (this.transferForm.invalid) {
      this.transferForm.markAllAsTouched();
      Swal.fire('Atención', 'Seleccione la entidad destino.', 'warning');
      return;
    }

    const processSlug = this.processSlug() || TRANSFER_SLUG;

    try {
      const processes = await firstValueFrom(this.catalogService.loadProcesses());
      this.catalogService.processes.set(processes ?? []);
    } catch { /* catálogo en memoria */ }

    if (this.bpmService.initialStepRequiresDocument(processSlug)) {
      const statusSlug = this.bpmService.initialStatusSlug(processSlug);
      this.approvalRequest.set({
        processSlug,
        statusSlug,
        statusObj: { slug: statusSlug },
        entityCode: this.partyData()?.entityCode ?? this.currentEntityCode() ?? null
      } as unknown as Request);
      setTimeout(() => this.approvalModal?.open(), 0);
      return;
    }

    const confirmed = await Swal.fire({
      title: '¿Confirmar Solicitud?',
      text: 'Se enviará una solicitud de transferencia a la entidad seleccionada.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, Enviar'
    });
    if (!confirmed.isConfirmed) return;

    await this._createRequest(null);
  }

  async onStepApprovalConfirmed(payload: StepApprovalPayload): Promise<void> {
    if (this.isSubmitting()) return;
    try {
      await this._createRequest(payload);
      this.approvalModal?.forceClose();
    } catch {
      /* error ya mostrado */
    }
  }

  private _buildPayload(bpmPayload: StepApprovalPayload | null): any {
    const party = this.partyData();
    return {
      partyUuid: party?.uuid,
      processSlug: this.processSlug() || TRANSFER_SLUG,
      entityCode: party?.entityCode ?? this.currentEntityCode(),
      targetEntityCode: this.transferForm.get('targetEntityCode')?.value,
      referenceUuid: this.transferForm.get('referenceUuid')?.value ?? party?.uuid,
      reason: this.transferForm.get('reason')?.value,
      voto: bpmPayload?.gloss ?? null,
      gloss: bpmPayload?.gloss ?? null,
      documentUuid: bpmPayload?.documentUuid ?? null,
      documentTypeName: bpmPayload?.documentTypeName ?? null,
      requiresDocument: bpmPayload?.requiresDocument ?? false
    };
  }

  private async _createRequest(bpmPayload: StepApprovalPayload | null): Promise<void> {
    this.isSubmitting.set(true);
    try {
      await firstValueFrom(
        this.partyOverviewService.createTransferRequest(this._buildPayload(bpmPayload))
      );
      this.approvalRequest.set(null);
      await Swal.fire({
        icon: 'success',
        title: 'Solicitud enviada',
        text: 'La transferencia quedó registrada según el flujo BPM.',
        timer: 1800,
        showConfirmButton: false
      });
      this.onSubmitted.emit();
    } catch (err: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Error al enviar',
        text: err?.error?.message ?? err?.message ?? 'No se pudo enviar la solicitud'
      });
      throw err;
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
